import {
  MULTI_FIELDS,
  SINGLE_FIELDS,
  type AggregatesArtifact,
  type AnnotatedCrosstab,
  type Codebook,
  type CodebookField,
  type EncodedResponse,
  type GeoLinearRing,
  type GeoMultiPolygonCoordinates,
  type JapanMapArtifact,
  type KansaiMapArtifact,
  type KansaiPrefectureCode,
  type KansaiPrefectureName,
  type ResponsesArtifact,
  type MethodologyArtifact,
  type StoryAggregates,
  type Voice,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function assertMethodologyArtifact(value: unknown): asserts value is MethodologyArtifact {
  invariant(isRecord(value), 'methodology.json must be an object');
  assertExactKeys(value, ['title', 'madeBy', 'collection', 'limitations', 'privacy', 'updatedAt'], 'methodology.json');
  invariant(typeof value.title === 'string' && typeof value.madeBy === 'string' && typeof value.updatedAt === 'string', 'methodology text is invalid');
  invariant(isRecord(value.collection), 'methodology.collection must be an object');
  assertExactKeys(value.collection, ['responses', 'days', 'context'], 'methodology.collection');
  assertNonNegativeInteger(value.collection.responses, 'methodology.collection.responses');
  assertNonNegativeInteger(value.collection.days, 'methodology.collection.days');
  invariant(typeof value.collection.context === 'string', 'methodology.collection.context is invalid');
  for (const key of ['limitations', 'privacy'] as const) {
    invariant(Array.isArray(value[key]) && value[key].every((item) => typeof item === 'string'), `methodology.${key} is invalid`);
  }
}

function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(`データ形式エラー: ${message}`);
}

function assertExactKeys(value: Record<string, unknown>, expected: readonly string[], label: string): void {
  const actual = Object.keys(value);
  const expectedSet = new Set(expected);
  const missing = expected.filter((key) => !(key in value));
  const extra = actual.filter((key) => !expectedSet.has(key));
  invariant(missing.length === 0, `${label} に必須項目 ${missing.join(', ')} がありません`);
  invariant(extra.length === 0, `${label} に許可されていない項目 ${extra.join(', ')} があります`);
}

function assertNonNegativeInteger(value: unknown, label: string): asserts value is number {
  invariant(Number.isInteger(value) && Number(value) >= 0, `${label} は0以上の整数ではありません`);
}

function assertFiniteNonNegativeNumber(value: unknown, label: string): asserts value is number {
  invariant(typeof value === 'number' && Number.isFinite(value) && value >= 0, `${label} は0以上の有限数ではありません`);
}

function assertRate(value: unknown, label: string): asserts value is number {
  assertFiniteNonNegativeNumber(value, label);
  invariant(value <= 100, `${label} は100以下ではありません`);
}

function assertNumberRecord(value: unknown, label: string): asserts value is Record<string, number> {
  invariant(isRecord(value), `${label} は集計オブジェクトではありません`);
  for (const [key, count] of Object.entries(value)) {
    assertNonNegativeInteger(count, `${label}.${key}`);
  }
}

function assertAnnotatedCrosstab(value: unknown, label: string): asserts value is AnnotatedCrosstab {
  invariant(isRecord(value), `${label} がありません`);
  assertExactKeys(value, ['cells', 'rowTotals', 'colTotals', 'total'], label);
  invariant(isRecord(value.cells), `${label}.cells がありません`);
  assertNumberRecord(value.rowTotals, `${label}.rowTotals`);
  assertNumberRecord(value.colTotals, `${label}.colTotals`);
  assertNonNegativeInteger(value.total, `${label}.total`);
  for (const [rowKey, row] of Object.entries(value.cells)) {
    invariant(isRecord(row), `${label}.cells.${rowKey} が不正です`);
    for (const [colKey, cell] of Object.entries(row)) {
      invariant(isRecord(cell), `${label}.cells.${rowKey}.${colKey} が不正です`);
      assertExactKeys(cell, ['n', 'small'], `${label}.cells.${rowKey}.${colKey}`);
      assertNonNegativeInteger(cell.n, `${label}.cells.${rowKey}.${colKey}.n`);
      invariant(typeof cell.small === 'boolean', `${label}.cells.${rowKey}.${colKey}.small が不正です`);
    }
  }
}

function assertPeriodCoverage(value: unknown, label: string): void {
  invariant(isRecord(value), `${label} がありません`);
  assertExactKeys(value, ['n', 'days'], label);
  assertNonNegativeInteger(value.n, `${label}.n`);
  invariant(
    Array.isArray(value.days) && value.days.every((day) => typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day)),
    `${label}.days が日付配列ではありません`,
  );
}

function assertStoryMetric(value: unknown, label: string): void {
  invariant(isRecord(value), `${label} が不正です`);
  assertExactKeys(value, ['n', 'rate'], label);
  assertNonNegativeInteger(value.n, `${label}.n`);
  assertRate(value.rate, `${label}.rate`);
}

function assertStoryAggregates(value: unknown): asserts value is StoryAggregates {
  invariant(isRecord(value), 'story が不正です');
  assertExactKeys(
    value,
    ['headlines', 'sankey', 'geography', 'facilityAwareness', 'desiredFacilities', 'heatComplaintCount', 'episodes', 'evidence', 'negativeThemes', 'positiveThemes', 'dataStories'],
    'story',
  );

  invariant(isRecord(value.headlines), 'story.headlines が不正です');
  const metricKeys = [
    'family', 'eventPurpose', 'lightVisitors', 'female', 'train',
    'positiveSatisfaction', 'verySatisfied', 'satisfied', 'officeFirst', 'facilityNone',
  ] as const;
  const scalarKeys = ['satisfactionAverage', 'firstVisitPositiveRate', 'firstVisitOfficeFirstRate'] as const;
  assertExactKeys(value.headlines, [...metricKeys, ...scalarKeys], 'story.headlines');
  for (const key of metricKeys) assertStoryMetric(value.headlines[key], `story.headlines.${key}`);
  assertFiniteNonNegativeNumber(value.headlines.satisfactionAverage, 'story.headlines.satisfactionAverage');
  assertRate(value.headlines.firstVisitPositiveRate, 'story.headlines.firstVisitPositiveRate');
  assertRate(value.headlines.firstVisitOfficeFirstRate, 'story.headlines.firstVisitOfficeFirstRate');
  const lockedMetrics: Record<string, [number, number]> = {
    family: [929, 67],
    eventPurpose: [913, 65.9],
    lightVisitors: [854, 61.6],
    female: [862, 62.2],
    train: [887, 64],
    positiveSatisfaction: [1225, 88.4],
    verySatisfied: [496, 35.8],
    satisfied: [729, 52.6],
    officeFirst: [736, 53.1],
    facilityNone: [876, 63.2],
  };
  for (const [key, [n, rate]] of Object.entries(lockedMetrics)) {
    const metric = value.headlines[key];
    invariant(isRecord(metric) && metric.n === n && metric.rate === rate, `story.headlines.${key} が確定値と一致しません`);
  }
  invariant(value.headlines.satisfactionAverage === 4.23, 'story.headlines.satisfactionAverage が4.23ではありません');
  invariant(value.headlines.firstVisitPositiveRate === 87.2, 'story.headlines.firstVisitPositiveRate が87.2ではありません');
  invariant(value.headlines.firstVisitOfficeFirstRate === 72.4, 'story.headlines.firstVisitOfficeFirstRate が72.4ではありません');

  invariant(isRecord(value.sankey), 'story.sankey が不正です');
  assertExactKeys(value.sankey, ['note', 'nodes', 'links'], 'story.sankey');
  invariant(value.sankey.note === '認知経路は複数回答のため按分', 'story.sankey.note が不正です');
  invariant(Array.isArray(value.sankey.nodes), 'story.sankey.nodes が配列ではありません');
  invariant(Array.isArray(value.sankey.links), 'story.sankey.links が配列ではありません');
  const expectedNodeIds = new Set([
    'awareness:known', 'awareness:search-ads', 'awareness:social',
    'awareness:word-of-mouth', 'awareness:offline', 'awareness:missing',
    'purpose:event', 'purpose:consumption', 'purpose:stay', 'purpose:daily-other',
    'satisfaction:positive', 'satisfaction:neutral', 'satisfaction:negative',
  ]);
  const nodeIds = new Set<string>();
  const stages = new Map<string, string>();
  for (const [index, node] of value.sankey.nodes.entries()) {
    invariant(isRecord(node), `story.sankey.nodes[${index}] が不正です`);
    assertExactKeys(node, ['id', 'label', 'stage', 'value'], `story.sankey.nodes[${index}]`);
    invariant(typeof node.id === 'string' && node.id.length > 0, `story.sankey.nodes[${index}].id が不正です`);
    invariant(!nodeIds.has(node.id), `story.sankey.nodes[${index}].id が重複しています`);
    invariant(typeof node.label === 'string' && node.label.length > 0, `story.sankey.nodes[${index}].label が不正です`);
    invariant(['awareness', 'purpose', 'satisfaction'].includes(String(node.stage)), `story.sankey.nodes[${index}].stage が不正です`);
    assertFiniteNonNegativeNumber(node.value, `story.sankey.nodes[${index}].value`);
    nodeIds.add(node.id);
    stages.set(node.id, String(node.stage));
  }
  invariant(
    nodeIds.size === expectedNodeIds.size && [...expectedNodeIds].every((id) => nodeIds.has(id)),
    'story.sankey.nodes の分類が不正です',
  );
  for (const [index, link] of value.sankey.links.entries()) {
    invariant(isRecord(link), `story.sankey.links[${index}] が不正です`);
    assertExactKeys(link, ['source', 'target', 'value'], `story.sankey.links[${index}]`);
    invariant(typeof link.source === 'string' && nodeIds.has(link.source), `story.sankey.links[${index}].source が不正です`);
    invariant(typeof link.target === 'string' && nodeIds.has(link.target), `story.sankey.links[${index}].target が不正です`);
    const sourceStage = stages.get(String(link.source));
    const targetStage = stages.get(String(link.target));
    invariant(
      (sourceStage === 'awareness' && targetStage === 'purpose') ||
        (sourceStage === 'purpose' && targetStage === 'satisfaction'),
      `story.sankey.links[${index}] の段階接続が不正です`,
    );
    assertFiniteNonNegativeNumber(link.value, `story.sankey.links[${index}].value`);
  }
  for (const [sourceStage, targetStage] of [['awareness', 'purpose'], ['purpose', 'satisfaction']] as const) {
    const total = value.sankey.links
      .filter((link) => stages.get(String(link.source)) === sourceStage && stages.get(String(link.target)) === targetStage)
      .reduce((sum, link) => sum + Number(link.value), 0);
    invariant(Math.abs(total - 1386) < 1e-6, `story.sankey ${sourceStage}→${targetStage} は1,386を保存していません`);
  }
  for (const node of value.sankey.nodes) {
    const id = String(node.id);
    const stage = String(node.stage);
    const incoming = value.sankey.links
      .filter((link) => link.target === id)
      .reduce((sum, link) => sum + Number(link.value), 0);
    const outgoing = value.sankey.links
      .filter((link) => link.source === id)
      .reduce((sum, link) => sum + Number(link.value), 0);
    const derived = stage === 'satisfaction' ? incoming : outgoing;
    invariant(Math.abs(Number(node.value) - derived) < 1e-6, `story.sankey node ${id} の値がリンクと一致しません`);
    if (stage === 'purpose') {
      invariant(Math.abs(incoming - outgoing) < 1e-6, `story.sankey node ${id} でフローが保存されていません`);
    }
  }

  invariant(isRecord(value.geography), 'story.geography が不正です');
  assertExactKeys(value.geography, ['validPostalN', 'prefectures', 'municipalities', 'localComparison'], 'story.geography');
  assertNonNegativeInteger(value.geography.validPostalN, 'story.geography.validPostalN');
  invariant(Array.isArray(value.geography.prefectures), 'story.geography.prefectures が配列ではありません');
  const prefectureIds = new Set<string>();
  for (const [index, prefecture] of value.geography.prefectures.entries()) {
    invariant(isRecord(prefecture), `story.geography.prefectures[${index}] が不正です`);
    assertExactKeys(prefecture, ['pref', 'n'], `story.geography.prefectures[${index}]`);
    invariant(typeof prefecture.pref === 'string' && prefecture.pref.length > 0, `story.geography.prefectures[${index}].pref が不正です`);
    assertNonNegativeInteger(prefecture.n, `story.geography.prefectures[${index}].n`);
    invariant(prefecture.n > 0 && !prefectureIds.has(prefecture.pref), `story.geography.prefectures[${index}] が重複または空です`);
    prefectureIds.add(prefecture.pref);
  }
  invariant(value.geography.prefectures.reduce((sum, prefecture) => sum + Number(prefecture.n), 0) === value.geography.validPostalN, 'story.geography.prefectures の合計がvalidPostalNと一致しません');
  invariant(Array.isArray(value.geography.municipalities), 'story.geography.municipalities が配列ではありません');
  const municipalityIds = new Set<string>();
  for (const [index, municipality] of value.geography.municipalities.entries()) {
    invariant(isRecord(municipality), `story.geography.municipalities[${index}] が不正です`);
    assertExactKeys(municipality, ['pref', 'city', 'n'], `story.geography.municipalities[${index}]`);
    invariant(typeof municipality.pref === 'string' && municipality.pref.length > 0, `story.geography.municipalities[${index}].pref が不正です`);
    invariant(typeof municipality.city === 'string' && municipality.city.length > 0, `story.geography.municipalities[${index}].city が不正です`);
    assertNonNegativeInteger(municipality.n, `story.geography.municipalities[${index}].n`);
    invariant(Number(municipality.n) > 0, `story.geography.municipalities[${index}].n は0です`);
    const municipalityId = `${String(municipality.pref)}\u0000${String(municipality.city)}`;
    invariant(!municipalityIds.has(municipalityId), `story.geography.municipalities[${index}] が重複しています`);
    municipalityIds.add(municipalityId);
  }
  invariant(
    value.geography.municipalities.reduce((sum, municipality) => sum + Number(municipality.n), 0) === value.geography.validPostalN,
    'story.geography.municipalities の合計がvalidPostalNと一致しません',
  );
  invariant(isRecord(value.geography.localComparison), 'story.geography.localComparison が不正です');
  assertExactKeys(
    value.geography.localComparison,
    ['localPrefix', 'localN', 'localLowN', 'localLowRate', 'outsideN', 'outsideLowN', 'outsideLowRate'],
    'story.geography.localComparison',
  );
  invariant(value.geography.localComparison.localPrefix === '559', 'story.geography.localComparison.localPrefix が不正です');
  for (const key of ['localN', 'localLowN', 'outsideN', 'outsideLowN'] as const) {
    assertNonNegativeInteger(value.geography.localComparison[key], `story.geography.localComparison.${key}`);
  }
  for (const key of ['localLowRate', 'outsideLowRate'] as const) {
    assertRate(value.geography.localComparison[key], `story.geography.localComparison.${key}`);
  }
  invariant(
    Number(value.geography.localComparison.localN) + Number(value.geography.localComparison.outsideN) === 1386,
    'story.geography.localComparison の母数が1,386ではありません',
  );
  const local = value.geography.localComparison;
  invariant(
    local.localN === 123 && local.localLowN === 4 && local.localLowRate === 3.3 &&
      local.outsideN === 1263 && local.outsideLowN === 8 && local.outsideLowRate === 0.6,
    'story.geography.localComparison が確定値と一致しません',
  );

  for (const key of ['facilityAwareness', 'desiredFacilities'] as const) {
    const entries = value[key];
    invariant(Array.isArray(entries), `story.${key} が配列ではありません`);
    const expectedLabels = key === 'facilityAwareness'
      ? ['大阪グリーンエコプラザ', 'ATCエイジレスセンター', 'ATC輸入住宅促進センター', '大阪デザイン振興プラザ']
      : ['飲食店、カフェ', '家族で楽しめる施設', '子どもが遊べる施設', '買い物ができる店舗', '休憩、リラックスできるスペース'];
    invariant(entries.length === expectedLabels.length, `story.${key} の項目数が不正です`);
    for (const [index, entry] of entries.entries()) {
      invariant(isRecord(entry), `story.${key}[${index}] が不正です`);
      assertExactKeys(entry, ['label', 'n'], `story.${key}[${index}]`);
      invariant(typeof entry.label === 'string' && entry.label.length > 0, `story.${key}[${index}].label が不正です`);
      invariant(entry.label === expectedLabels[index], `story.${key}[${index}].label が不正です`);
      assertNonNegativeInteger(entry.n, `story.${key}[${index}].n`);
    }
  }
  assertNonNegativeInteger(value.heatComplaintCount, 'story.heatComplaintCount');
  invariant(value.heatComplaintCount === 20, 'story.heatComplaintCount は20ではありません');
  invariant(isRecord(value.episodes), 'story.episodes が不正です');
  assertExactKeys(value.episodes, ['askedN', 'usableN', 'longN'], 'story.episodes');
  for (const key of ['askedN', 'usableN', 'longN'] as const) {
    assertNonNegativeInteger(value.episodes[key], `story.episodes.${key}`);
  }
  invariant(
    Number(value.episodes.longN) <= Number(value.episodes.usableN) &&
      Number(value.episodes.usableN) <= Number(value.episodes.askedN),
    'story.episodes の件数関係が不正です',
  );
  invariant(
    value.episodes.askedN === 111 && value.episodes.usableN === 93 && value.episodes.longN === 8,
    'story.episodes が確定値111/93/8と一致しません',
  );
  invariant(Array.isArray(value.evidence) && value.evidence.length >= 8, 'story.evidence must contain the audited claims');
  const evidenceIds = new Set<string>();
  for (const [index, claim] of value.evidence.entries()) {
    invariant(isRecord(claim), `story.evidence[${index}] must be an object`);
    assertExactKeys(claim, ['id', 'label', 'value', 'unit', 'numerator', 'denominator', 'source', 'caveat', 'small', 'handoff'], `story.evidence[${index}]`);
    invariant(typeof claim.id === 'string' && claim.id.length > 0 && !evidenceIds.has(claim.id), `story.evidence[${index}].id must be unique`);
    evidenceIds.add(claim.id);
    invariant(typeof claim.label === 'string' && typeof claim.value === 'string', `story.evidence[${index}] requires text`);
    invariant(['people', 'percent', 'score', 'voices', 'statement'].includes(String(claim.unit)), `story.evidence[${index}].unit is invalid`);
    invariant(['coded-response', 'reviewed-voice-theme', 'brand-document', 'editorial-synthesis'].includes(String(claim.source)), `story.evidence[${index}].source is invalid`);
    invariant(claim.numerator === null || (Number.isInteger(claim.numerator) && Number(claim.numerator) >= 0), `story.evidence[${index}].numerator is invalid`);
    invariant(claim.denominator === null || (Number.isInteger(claim.denominator) && Number(claim.denominator) >= 0), `story.evidence[${index}].denominator is invalid`);
    invariant(claim.caveat === null || typeof claim.caveat === 'string', `story.evidence[${index}].caveat is invalid`);
    invariant(typeof claim.small === 'boolean', `story.evidence[${index}].small is invalid`);
    invariant(claim.handoff === null || (isRecord(claim.handoff) && typeof claim.handoff.label === 'string' && ['audience', 'visit', 'satisfaction', 'awareness', 'needs'].includes(String(claim.handoff.tab))), `story.evidence[${index}].handoff is invalid`);
  }
  for (const id of ['sample', 'satisfaction-positive', 'facility-none', 'office-first', 'local-low-satisfaction', 'exact-heat', 'office-inquiry', 'solo-satisfied', 'solo-very-satisfied', 'brand-yohaku', 'synthesis']) {
    invariant(evidenceIds.has(id), `story.evidence must contain ${id}`);
  }
  invariant(Array.isArray(value.negativeThemes) && value.negativeThemes.length === 8, 'story.negativeThemes must contain the reviewed taxonomy');
  for (const [index, theme] of value.negativeThemes.entries()) {
    invariant(isRecord(theme), `story.negativeThemes[${index}] must be an object`);
    assertExactKeys(theme, ['id', 'label', 'n', 'denominator', 'definition'], `story.negativeThemes[${index}]`);
    invariant(typeof theme.id === 'string' && typeof theme.label === 'string' && typeof theme.definition === 'string', `story.negativeThemes[${index}] requires text`);
    assertNonNegativeInteger(theme.n, `story.negativeThemes[${index}].n`);
    assertNonNegativeInteger(theme.denominator, `story.negativeThemes[${index}].denominator`);
  }
  invariant(Array.isArray(value.positiveThemes) && value.positiveThemes.length === 5, 'story.positiveThemes must contain the reviewed brand strengths');
  for (const [index, theme] of value.positiveThemes.entries()) {
    invariant(isRecord(theme), `story.positiveThemes[${index}] must be an object`);
    assertExactKeys(theme, ['id', 'label', 'n', 'denominator', 'definition'], `story.positiveThemes[${index}]`);
    invariant(typeof theme.id === 'string' && typeof theme.label === 'string' && typeof theme.definition === 'string', `story.positiveThemes[${index}] requires text`);
    assertNonNegativeInteger(theme.n, `story.positiveThemes[${index}].n`);
    assertNonNegativeInteger(theme.denominator, `story.positiveThemes[${index}].denominator`);
  }
  invariant(Array.isArray(value.dataStories) && value.dataStories.length === 3, 'story.dataStories must contain three audited slides');
  for (const [index, slide] of value.dataStories.entries()) {
    invariant(isRecord(slide), `story.dataStories[${index}] must be an object`);
    assertExactKeys(slide, ['id', 'marker', 'question', 'title', 'conclusion', 'evidenceIds', 'dashboardTab'], `story.dataStories[${index}]`);
    invariant(['event-conversion', 'solo-experience', 'brand-yohaku'].includes(String(slide.id)), `story.dataStories[${index}].id is invalid`);
    invariant(typeof slide.marker === 'string' && typeof slide.question === 'string' && typeof slide.title === 'string' && typeof slide.conclusion === 'string', `story.dataStories[${index}] requires text`);
    invariant(Array.isArray(slide.evidenceIds) && slide.evidenceIds.every((id) => typeof id === 'string' && evidenceIds.has(id)), `story.dataStories[${index}].evidenceIds are invalid`);
    invariant(['audience', 'visit', 'satisfaction', 'awareness', 'needs'].includes(String(slide.dashboardTab)), `story.dataStories[${index}].dashboardTab is invalid`);
  }
}

const AGGREGATE_KEYS = [
  'total',
  ...SINGLE_FIELDS,
  ...MULTI_FIELDS,
  'daily',
  'hourly',
  'crosstabs',
  'smallNThreshold',
  'geography',
  'periodLimited',
  'story',
] as const;

export function assertAggregatesArtifact(value: unknown): asserts value is AggregatesArtifact {
  invariant(isRecord(value), 'aggregates.json のルートが不正です');
  assertExactKeys(value, AGGREGATE_KEYS, 'aggregates.json');
  invariant(value.total === 1386, `回答数が1,386ではありません (${String(value.total)})`);

  for (const field of [...SINGLE_FIELDS, ...MULTI_FIELDS]) assertNumberRecord(value[field], field);
  assertNumberRecord(value.daily, 'daily');
  assertNumberRecord(value.hourly, 'hourly');

  invariant(isRecord(value.crosstabs), 'crosstabs がありません');
  const crosstabKeys = [
    'age_satisfaction',
    'frequency_satisfaction',
    'companion_satisfaction',
    'transport_satisfaction',
  ] as const;
  assertExactKeys(value.crosstabs, crosstabKeys, 'crosstabs');
  for (const key of crosstabKeys) assertAnnotatedCrosstab(value.crosstabs[key], `crosstabs.${key}`);

  invariant(value.smallNThreshold === 30, 'smallNThreshold は30ではありません');
  invariant(isRecord(value.geography), 'geography がありません');
  assertExactKeys(value.geography, ['requiredPrefixCount', 'observedPrefixCount'], 'geography');
  invariant(value.geography.requiredPrefixCount === 167, 'requiredPrefixCount は167ではありません');
  assertNonNegativeInteger(value.geography.observedPrefixCount, 'geography.observedPrefixCount');

  invariant(isRecord(value.periodLimited), 'periodLimited がありません');
  assertExactKeys(value.periodLimited, ['visit_budget', 'atc_episode'], 'periodLimited');
  assertPeriodCoverage(value.periodLimited.visit_budget, 'periodLimited.visit_budget');
  assertPeriodCoverage(value.periodLimited.atc_episode, 'periodLimited.atc_episode');
  assertStoryAggregates(value.story);
}

const RESPONSE_KEYS = [
  'id',
  ...SINGLE_FIELDS,
  ...MULTI_FIELDS,
  'postal',
  'day',
  'hour',
] as const;
const CODEBOOK_KEYS = [...SINGLE_FIELDS, ...MULTI_FIELDS, 'day'] as const;

function assertCodebook(value: unknown): asserts value is Codebook {
  invariant(isRecord(value), 'codebook がありません');
  assertExactKeys(value, CODEBOOK_KEYS, 'codebook');
  for (const field of CODEBOOK_KEYS as readonly CodebookField[]) {
    const entries = value[field];
    invariant(
      Array.isArray(entries) && entries.length > 0 && entries.every((entry) => typeof entry === 'string'),
      `codebook.${field} が不正です`,
    );
    invariant(new Set(entries).size === entries.length, `codebook.${field} に重複があります`);
  }
  const requireExactDomain = (field: 'visit_frequency' | 'age_group', expected: readonly string[]) => {
    const actual = value[field] as string[];
    invariant(
      actual.length === expected.length && expected.every((label) => actual.includes(label)),
      `codebook.${field} が確定した選択肢と一致しません`,
    );
  };
  requireExactDomain('visit_frequency', [
    '今回が初めて', '年に1回程度', '年に2～3回程度', '月に1回程度',
    '月に2～3回程度', '週に1回以上', 'ほぼ毎日',
  ]);
  requireExactDomain('age_group', ['10代以下', '20代', '30代', '40代', '50代', '60代', '70代以上']);
}

function assertResponse(value: unknown, index: number, codebook: Codebook): asserts value is EncodedResponse {
  invariant(isRecord(value), `responses.rows[${index}] がオブジェクトではありません`);
  assertExactKeys(value, RESPONSE_KEYS, `responses.rows[${index}]`);
  invariant(Number.isInteger(value.id) && Number(value.id) > 0, `responses.rows[${index}].id が不正です`);
  for (const field of SINGLE_FIELDS) {
    const code = value[field];
    invariant(
      Number.isInteger(code) && Number(code) >= -1 && Number(code) < codebook[field].length,
      `responses.rows[${index}].${field} がコード範囲外です`,
    );
  }
  for (const field of MULTI_FIELDS) {
    const codes = value[field];
    invariant(
      Array.isArray(codes) &&
        codes.every((code) => Number.isInteger(code) && code >= 0 && code < codebook[field].length),
      `responses.rows[${index}].${field} がコード範囲外です`,
    );
  }
  invariant(
    value.postal === null || (typeof value.postal === 'string' && /^\d{3}$/.test(value.postal)),
    `responses.rows[${index}].postal は3桁ではありません`,
  );
  invariant(
    Number.isInteger(value.day) && Number(value.day) >= 0 && Number(value.day) < codebook.day.length,
    `responses.rows[${index}].day がコード範囲外です`,
  );
  invariant(Number.isInteger(value.hour) && Number(value.hour) >= 0 && Number(value.hour) <= 23, `responses.rows[${index}].hour が不正です`);
}

export function assertResponsesArtifact(value: unknown): asserts value is ResponsesArtifact {
  invariant(isRecord(value), 'responses.json のルートが不正です');
  assertExactKeys(value, ['rows', 'codebook'], 'responses.json');
  invariant(Array.isArray(value.rows), 'responses.rows がありません');
  invariant(value.rows.length === 1386, `responses.rows は1,386件ではありません (${value.rows.length})`);
  const codebook = value.codebook;
  assertCodebook(codebook);
  const ids = new Set<number>();
  value.rows.forEach((row, index) => {
    assertResponse(row, index, codebook);
    invariant(!ids.has(row.id), `responses.rows[${index}].id が重複しています`);
    ids.add(row.id);
  });
}

const VOICE_KEYS = [
  'id',
  'source',
  'text',
  'length',
  'themes',
  'sentiment',
  'isRequest',
  'heroCandidate',
  'gender',
  'age',
  'satisfaction',
  'frequency',
  'postal',
  'day',
] as const;

export function assertVoicesArtifact(value: unknown): asserts value is Voice[] {
  invariant(Array.isArray(value), 'voices.json のルートが配列ではありません');
  invariant(value.length === 1633, `voices.json は1,633件ではありません (${value.length})`);
  for (const [index, voice] of value.entries()) {
    invariant(isRecord(voice), `voices[${index}] が不正です`);
    assertExactKeys(voice, VOICE_KEYS, `voices[${index}]`);
    invariant(typeof voice.id === 'string' && voice.id.length > 0, `voices[${index}].id が不正です`);
    invariant(['atc_image', 'atc_episode', 'comments'].includes(String(voice.source)), `voices[${index}].source が不正です`);
    invariant(typeof voice.text === 'string' && voice.text.length > 0, `voices[${index}].text が不正です`);
    invariant(!/(^|\D)\d{7}(\D|$)/.test(voice.text), `voices[${index}] に7桁の番号が含まれています`);
    invariant(
      !/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(voice.text) &&
        !/(?:0\d{1,4}[-ー－]\d{1,4}[-ー－]\d{3,4})/.test(voice.text) &&
        !/(?<!\d)0\d{9,10}(?!\d)/.test(voice.text),
      `voices[${index}] に連絡先情報の可能性がある文字列が含まれています`,
    );
    assertNonNegativeInteger(voice.length, `voices[${index}].length`);
    invariant(voice.length === voice.text.length, `voices[${index}].length が本文と一致しません`);
    invariant(
      Array.isArray(voice.themes) && voice.themes.every((theme) => typeof theme === 'string'),
      `voices[${index}].themes が不正です`,
    );
    invariant(['positive', 'negative', 'neutral'].includes(String(voice.sentiment)), `voices[${index}].sentiment が不正です`);
    invariant(typeof voice.isRequest === 'boolean', `voices[${index}].isRequest が不正です`);
    invariant(typeof voice.heroCandidate === 'boolean', `voices[${index}].heroCandidate が不正です`);
    for (const field of ['gender', 'age', 'satisfaction', 'frequency'] as const) {
      invariant(typeof voice[field] === 'string', `voices[${index}].${field} が不正です`);
    }
    invariant(
      voice.postal === null || (typeof voice.postal === 'string' && /^\d{3}$/.test(voice.postal)),
      `voices[${index}].postal は3桁ではありません`,
    );
    invariant(typeof voice.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(voice.day), `voices[${index}].day が不正です`);
  }
}

const KANSAI_PREFECTURES = {
  '25': '滋賀県',
  '26': '京都府',
  '27': '大阪府',
  '28': '兵庫県',
  '29': '奈良県',
  '30': '和歌山県',
} as const satisfies Record<KansaiPrefectureCode, KansaiPrefectureName>;

function assertPosition(value: unknown, label: string, nationwide = false): asserts value is [number, number] {
  invariant(
    Array.isArray(value) &&
      value.length === 2 &&
      value.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate)),
    `${label} は経度・緯度の2数値である必要があります`,
  );
  invariant(value[0] >= (nationwide ? 120 : 130) && value[0] <= (nationwide ? 155 : 140), `${label} の経度が対象域外です`);
  invariant(value[1] >= (nationwide ? 20 : 30) && value[1] <= (nationwide ? 50 : 40), `${label} の緯度が対象域外です`);
}

function assertLinearRing(value: unknown, label: string, nationwide = false): asserts value is GeoLinearRing {
  invariant(Array.isArray(value) && value.length >= 4, `${label} は4点以上の閉じたリングである必要があります`);
  value.forEach((position, index) => assertPosition(position, `${label}[${index}]`, nationwide));
  const first = value[0]!;
  const last = value[value.length - 1]!;
  invariant(first[0] === last[0] && first[1] === last[1], `${label} が閉じていません`);
}

function assertMultiPolygon(value: unknown, label: string, nationwide = false): asserts value is GeoMultiPolygonCoordinates {
  invariant(Array.isArray(value) && value.length > 0, `${label} は空でないMultiPolygonである必要があります`);
  value.forEach((polygon, polygonIndex) => {
    invariant(Array.isArray(polygon) && polygon.length > 0, `${label}[${polygonIndex}] は空です`);
    polygon.forEach((ring, ringIndex) =>
      assertLinearRing(ring, `${label}[${polygonIndex}][${ringIndex}]`, nationwide),
    );
  });
}

export function assertKansaiMapArtifact(value: unknown): asserts value is KansaiMapArtifact {
  invariant(isRecord(value), '関西地図のルートが不正です');
  assertExactKeys(value, ['type', 'name', 'source', 'bbox', 'features'], '関西地図');
  invariant(value.type === 'FeatureCollection', '関西地図はFeatureCollectionである必要があります');
  invariant(typeof value.name === 'string' && value.name.length > 0, '関西地図のnameが不正です');

  invariant(isRecord(value.source), '関西地図のsourceが不正です');
  assertExactKeys(
    value.source,
    ['title', 'url', 'date', 'license', 'attribution', 'modified'],
    '関西地図.source',
  );
  for (const key of ['title', 'url', 'attribution', 'modified'] as const) {
    invariant(typeof value.source[key] === 'string' && value.source[key].length > 0, `関西地図.source.${key}が不正です`);
  }
  invariant(
    value.source.url === 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2026.html',
    '関西地図の出典URLが不正です',
  );
  invariant(value.source.date === '2026-01-01', '関西地図の基準日が不正です');
  invariant(value.source.license === 'CC BY 4.0', '関西地図のライセンス表記が不正です');

  invariant(Array.isArray(value.bbox) && value.bbox.length === 4, '関西地図のbboxが不正です');
  value.bbox.forEach((coordinate, index) =>
    invariant(typeof coordinate === 'number' && Number.isFinite(coordinate), `関西地図.bbox[${index}]が不正です`),
  );
  invariant(value.bbox[0] < value.bbox[2] && value.bbox[1] < value.bbox[3], '関西地図のbbox順序が不正です');

  invariant(Array.isArray(value.features) && value.features.length > 0, '関西地図のfeaturesが空です');
  const municipalityCodes = new Set<string>();
  const observedPrefectures = new Set<string>();
  value.features.forEach((feature, index) => {
    invariant(isRecord(feature), `関西地図.features[${index}]が不正です`);
    assertExactKeys(feature, ['type', 'id', 'properties', 'geometry'], `関西地図.features[${index}]`);
    invariant(feature.type === 'Feature', `関西地図.features[${index}].typeが不正です`);
    invariant(typeof feature.id === 'string', `関西地図.features[${index}].idが不正です`);
    invariant(isRecord(feature.properties), `関西地図.features[${index}].propertiesが不正です`);
    assertExactKeys(
      feature.properties,
      ['prefectureCode', 'prefectureName', 'municipalityCode', 'municipalityName'],
      `関西地図.features[${index}].properties`,
    );
    const prefectureCode = feature.properties.prefectureCode;
    invariant(
      typeof prefectureCode === 'string' && prefectureCode in KANSAI_PREFECTURES,
      `関西地図.features[${index}].prefectureCodeが不正です`,
    );
    const expectedPrefecture = KANSAI_PREFECTURES[prefectureCode as KansaiPrefectureCode];
    invariant(feature.properties.prefectureName === expectedPrefecture, `関西地図.features[${index}]の府県名がコードと一致しません`);
    invariant(
      typeof feature.properties.municipalityCode === 'string' &&
        new RegExp(`^${prefectureCode}\\d{3}$`).test(feature.properties.municipalityCode),
      `関西地図.features[${index}].municipalityCodeが不正です`,
    );
    invariant(feature.id === feature.properties.municipalityCode, `関西地図.features[${index}]のidが自治体コードと一致しません`);
    invariant(!municipalityCodes.has(feature.id), `関西地図の自治体コード${feature.id}が重複しています`);
    municipalityCodes.add(feature.id);
    observedPrefectures.add(prefectureCode);
    invariant(
      typeof feature.properties.municipalityName === 'string' && feature.properties.municipalityName.length > 0,
      `関西地図.features[${index}].municipalityNameが不正です`,
    );

    invariant(isRecord(feature.geometry), `関西地図.features[${index}].geometryが不正です`);
    assertExactKeys(feature.geometry, ['type', 'coordinates'], `関西地図.features[${index}].geometry`);
    invariant(feature.geometry.type === 'MultiPolygon', `関西地図.features[${index}]はMultiPolygonではありません`);
    assertMultiPolygon(feature.geometry.coordinates, `関西地図.features[${index}].geometry.coordinates`);
  });
  invariant(
    Object.keys(KANSAI_PREFECTURES).every((code) => observedPrefectures.has(code)),
    '関西地図に6府県すべてが含まれていません',
  );
}

/** Keep nationwide geometry on the same strictly bounded browser-data boundary. */
export function assertJapanMapArtifact(value: unknown): asserts value is JapanMapArtifact {
  invariant(isRecord(value), '全国地図のルートが不正です');
  assertExactKeys(value, ['type', 'name', 'source', 'bbox', 'features'], '全国地図');
  invariant(value.type === 'FeatureCollection', '全国地図はFeatureCollectionである必要があります');
  invariant(typeof value.name === 'string' && value.name.length > 0, '全国地図の名称が不正です');
  invariant(isRecord(value.source), '全国地図の出典が不正です');
  assertExactKeys(value.source, ['title', 'url', 'date', 'license', 'attribution', 'modified'], '全国地図.source');
  invariant(value.source.url === 'https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2026.html', '全国地図の出典URLが不正です');
  invariant(value.source.date === '2026-01-01' && value.source.license === 'CC BY 4.0', '全国地図の出典情報が不正です');
  invariant(Array.isArray(value.bbox) && value.bbox.length === 4 && value.bbox.every((item) => typeof item === 'number' && Number.isFinite(item)), '全国地図の範囲が不正です');
  invariant(value.bbox[0] < value.bbox[2] && value.bbox[1] < value.bbox[3], '全国地図の範囲順が不正です');
  invariant(Array.isArray(value.features) && value.features.length === 47, '全国地図は47都道府県である必要があります');
  const codes = new Set<string>();
  for (const [index, feature] of value.features.entries()) {
    invariant(isRecord(feature), `全国地図.features[${index}]が不正です`);
    assertExactKeys(feature, ['type', 'id', 'properties', 'geometry'], `全国地図.features[${index}]`);
    invariant(feature.type === 'Feature' && typeof feature.id === 'string', `全国地図.features[${index}]の識別子が不正です`);
    invariant(isRecord(feature.properties), `全国地図.features[${index}]の属性が不正です`);
    assertExactKeys(feature.properties, ['prefectureCode', 'prefectureName'], `全国地図.features[${index}].properties`);
    const code = feature.properties.prefectureCode;
    invariant(typeof code === 'string' && /^\d{2}$/.test(code), `全国地図.features[${index}]の都道府県コードが不正です`);
    invariant(feature.id === code && !codes.has(code), `全国地図.features[${index}]の都道府県コードが重複または不一致です`);
    codes.add(code);
    invariant(typeof feature.properties.prefectureName === 'string' && feature.properties.prefectureName.length > 0, `全国地図.features[${index}]の都道府県名が不正です`);
    invariant(isRecord(feature.geometry), `全国地図.features[${index}]の形状が不正です`);
    assertExactKeys(feature.geometry, ['type', 'coordinates'], `全国地図.features[${index}].geometry`);
    invariant(feature.geometry.type === 'MultiPolygon', `全国地図.features[${index}]はMultiPolygonである必要があります`);
    assertMultiPolygon(feature.geometry.coordinates, `全国地図.features[${index}].geometry.coordinates`, true);
  }
  invariant(codes.size === 47, '全国地図の都道府県コードが不足しています');
}
